import React, { useState } from 'react';

const DECISION_OPTIONS = ['Rehost', 'Replatform', 'Refactor', 'Repurchase', 'Relocate', 'Retain', 'Retire'];

const OPT_COLOR = {
  Rehost: '#06B6D4', Replatform: '#8B5CF6', Refactor: '#9A6700',
  Repurchase: '#6639BA', Relocate: '#57606A', Retain: '#94A3B8', Retire: '#CF222E',
  // Legacy compat
  New: '#1A7F37', Replace: '#6639BA',
};
const OPT_BG = {
  Rehost: '#ECFEFF', Replatform: '#F5F3FF', Refactor: '#FFF8C5',
  Repurchase: '#FBEFFF', Relocate: '#F6F8FA', Retain: '#F6F8FA', Retire: '#FFEBE9',
  New: '#DAFBE1', Replace: '#FBEFFF',
};
const OPT_ICON = {
  Rehost: '☁️', Replatform: '🐳', Refactor: '🔧',
  Repurchase: '🛒', Relocate: '📦', Retain: '🏠', Retire: '🗑️',
  New: '🆕', Replace: '🛒',
};

export default function DecisionGate({ data, onSubmit }) {
  const recs = data?.recommendations || [];

  const [decisions, setDecisions] = useState(() =>
    Object.fromEntries(recs.map(r => [r.name, r.recommendation]))
  );
  const [selected, setSelected]   = useState(() => new Set(recs.map(r => r.name)));
  // selectedModules: { appName: Set<moduleId> } — empty Set means "all modules"
  const [selectedModules, setSelectedModules] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Sync decisions and selected when recs arrive (handles SSE replay where recs populate after mount)
  React.useEffect(() => {
    if (recs.length > 0) {
      setDecisions(prev => {
        const next = { ...prev };
        recs.forEach(r => {
          if (!next[r.name]) next[r.name] = r.recommendation;
        });
        return next;
      });
      setSelected(prev => {
        if (prev.size === 0) return new Set(recs.map(r => r.name));
        return prev;
      });
    }
  }, [recs.length]); // eslint-disable-line

  const toggleSelect = (name) => setSelected(prev => {
    const next = new Set(prev);
    next.has(name) ? next.delete(name) : next.add(name);
    return next;
  });

  const handleSubmit = async () => {
    setSubmitting(true);
    const finalDecisions = Object.fromEntries(
      recs.map(r => [r.name, selected.has(r.name) ? decisions[r.name] : 'Retire'])
    );
    // Convert Set → Array for each app; empty array = all modules
    const finalModules = Object.fromEntries(
      recs.map(r => [r.name, selectedModules[r.name] ? [...selectedModules[r.name]] : []])
    );
    await onSubmit(finalDecisions, finalModules);
  };

  const selectedCount  = selected.size;
  const retireCount    = [...selected].filter(n => decisions[n] === 'Retire').length;
  const refactorCount  = [...selected].filter(n => decisions[n] === 'Refactor').length;
  const newCount       = [...selected].filter(n => decisions[n] === 'New').length;
  const replaceCount   = [...selected].filter(n => decisions[n] === 'Replace').length;
  const skippedCount   = recs.length - selectedCount;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 2000,
      background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      <div style={{
        background: '#fff', borderRadius: 14,
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        width: '100%', maxWidth: 960, maxHeight: '92vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        animation: 'modal-in 0.2s ease',
      }}>
        <style>{`@keyframes modal-in { from { opacity:0; transform:translateY(-16px) scale(0.98); } to { opacity:1; transform:none; } }`}</style>

        {/* Header */}
        <div style={{ padding: '20px 28px 16px', borderBottom: '1px solid #D0D7DE',
          background: '#FFF8C5', borderRadius: '14px 14px 0 0' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 22 }}>⏸</span>
              <div>
                <div style={{ fontWeight: 800, fontSize: 16, color: '#9A6700' }}>
                  Your Input Needed — Application Decisions
                </div>
                <div style={{ fontSize: 12, color: '#57606A', marginTop: 2 }}>
                  Review the AI scoring below. Each option was independently evaluated — including COTS/SaaS replacements. Change any decision before continuing.
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexShrink: 0, marginLeft: 16 }}>
              <button onClick={() => setSelected(new Set(recs.map(r => r.name)))} style={{
                fontSize: 11, padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
                border: '1px solid #D0D7DE', background: '#fff', color: '#57606A' }}>
                Select all
              </button>
              <button onClick={() => setSelected(new Set())} style={{
                fontSize: 11, padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
                border: '1px solid #D0D7DE', background: '#fff', color: '#57606A' }}>
                Deselect all
              </button>
            </div>
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', padding: '20px 28px', flex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', gap: 16 }}>
            {recs.map(app => (
              <AppDecisionCard
                key={app.name} app={app}
                decision={decisions[app.name]}
                isSelected={selected.has(app.name)}
                onToggleSelect={() => toggleSelect(app.name)}
                onChange={dec => setDecisions(prev => ({ ...prev, [app.name]: dec }))}
                disabled={submitting}
                selectedModules={selectedModules[app.name]}
                onModulesChange={mods => setSelectedModules(prev => ({ ...prev, [app.name]: mods }))}
              />
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: '14px 28px', borderTop: '1px solid #D0D7DE', background: '#F6F8FA',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 16, borderRadius: '0 0 14px 14px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <Chip color="#0969DA"  label={`${selectedCount} selected`} />
            {retireCount   > 0 && <Chip color="#CF222E" label={`${retireCount} retire`} />}
            {refactorCount > 0 && <Chip color="#9A6700" label={`${refactorCount} refactor`} />}
            {newCount      > 0 && <Chip color="#1A7F37" label={`${newCount} rebuild`} />}
            {replaceCount  > 0 && <Chip color="#6639BA" label={`${replaceCount} replace`} />}
            {skippedCount  > 0 && <Chip color="#57606A" label={`${skippedCount} skipped`} />}
          </div>
          {submitting ? (
            <span style={{ color: '#57606A', fontSize: 13 }}>
              <span className="spin" style={{ display: 'inline-block', marginRight: 6 }}>⟳</span>
              Saving decisions…
            </span>
          ) : (
            <button className="btn btn-blue" onClick={handleSubmit}
              disabled={selectedCount === 0}
              title={selectedCount === 0 ? 'Select at least one application' : ''}>
              ✅ Confirm &amp; Continue ({selectedCount} app{selectedCount !== 1 ? 's' : ''})
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Chip({ color, label }) {
  return (
    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 10,
      background: color + '18', color, border: `1px solid ${color}44` }}>
      {label}
    </span>
  );
}

/* ── Migration Plan panel (expandable, per-R strategy details) ───────────── */
function MigrationPlanPanel({ plan, opt, optColor }) {
  const [open, setOpen] = React.useState(false);
  if (!plan || Object.keys(plan).length === 0) return null;

  return (
    <div style={{ padding: '0 10px 8px 38px' }}>
      <div onClick={() => setOpen(!open)} style={{
        cursor: 'pointer', fontSize: 11, fontWeight: 600, color: optColor,
        display: 'flex', alignItems: 'center', gap: 4, padding: '4px 0',
      }}>
        <span style={{ transform: open ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', fontSize: 9 }}>▶</span>
        📋 Migration Plan — {opt}
        <span style={{ fontSize: 9, color: '#8b949e', fontWeight: 400, marginLeft: 4 }}>
          {plan.timeline || ''}
        </span>
      </div>
      {open && (
        <div style={{ background: '#F6F8FA', border: '1px solid #D0D7DE', borderRadius: 8, padding: '10px 12px', marginTop: 4, fontSize: 11 }}>
          {plan.approach && (
            <div style={{ fontWeight: 600, color: '#1A1F2E', marginBottom: 8 }}>{plan.approach}</div>
          )}
          {/* Key details */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            {plan.target_infra && <span style={{ padding: '2px 7px', borderRadius: 4, background: '#DAFBE1', color: '#1A7F37', fontSize: 10, fontWeight: 600 }}>Infra: {plan.target_infra}</span>}
            {plan.container_strategy && <span style={{ padding: '2px 7px', borderRadius: 4, background: '#F5F3FF', color: '#8B5CF6', fontSize: 10, fontWeight: 600 }}>🐳 {plan.container_strategy}</span>}
            {plan.database && <span style={{ padding: '2px 7px', borderRadius: 4, background: '#ECFEFF', color: '#06B6D4', fontSize: 10, fontWeight: 600 }}>DB: {plan.database}</span>}
            {plan.target_stack && <span style={{ padding: '2px 7px', borderRadius: 4, background: '#FFF8C5', color: '#9A6700', fontSize: 10, fontWeight: 600 }}>Stack: {plan.target_stack}</span>}
            {plan.architecture && <span style={{ padding: '2px 7px', borderRadius: 4, background: '#EFF2F5', color: '#57606A', fontSize: 10, fontWeight: 600 }}>{plan.architecture}</span>}
            {plan.recommended_product && <span style={{ padding: '2px 7px', borderRadius: 4, background: '#FBEFFF', color: '#6639BA', fontSize: 10, fontWeight: 600 }}>🛒 {plan.recommended_product}</span>}
            {plan.imaging_strategy && <span style={{ padding: '2px 7px', borderRadius: 4, background: '#EFF2F5', color: '#57606A', fontSize: 10, fontWeight: 600 }}>📦 {plan.imaging_strategy}</span>}
            {plan.cost_model && <span style={{ padding: '2px 7px', borderRadius: 4, background: '#FFF8C5', color: '#9A6700', fontSize: 10, fontWeight: 600 }}>💰 {plan.cost_model}</span>}
            {plan.eol_timeline && <span style={{ padding: '2px 7px', borderRadius: 4, background: '#FFEBE9', color: '#CF222E', fontSize: 10, fontWeight: 600 }}>⏰ EOL: {plan.eol_timeline}</span>}
            {plan.architecture_principle && <span style={{ padding: '2px 7px', borderRadius: 4, background: '#DBEAFE', color: '#1D4ED8', fontSize: 10, fontWeight: 600 }}>🏛️ {plan.architecture_principle}</span>}
            {plan.target_platform && <span style={{ padding: '2px 7px', borderRadius: 4, background: '#ECFEFF', color: '#06B6D4', fontSize: 10, fontWeight: 600 }}>☁️ {plan.target_platform}</span>}
            {plan.integration_approach && <span style={{ padding: '2px 7px', borderRadius: 4, background: '#F5F3FF', color: '#8B5CF6', fontSize: 10, fontWeight: 600 }}>🔗 {plan.integration_approach}</span>}
            {plan.hybrid_connectivity && <span style={{ padding: '2px 7px', borderRadius: 4, background: '#EFF2F5', color: '#57606A', fontSize: 10, fontWeight: 600 }}>🌐 {plan.hybrid_connectivity}</span>}
            {plan.network && <span style={{ padding: '2px 7px', borderRadius: 4, background: '#ECFEFF', color: '#06B6D4', fontSize: 10, fontWeight: 600 }}>🔌 {plan.network}</span>}
            {plan.suggested_stack && <span style={{ padding: '2px 7px', borderRadius: 4, background: '#FFF8C5', color: '#9A6700', fontSize: 10, fontWeight: 600 }}>→ {plan.suggested_stack}</span>}
            {plan.data_archival && <span style={{ padding: '2px 7px', borderRadius: 4, background: '#EFF2F5', color: '#57606A', fontSize: 10, fontWeight: 600 }}>📁 Archive: {plan.data_archival}</span>}
            {plan.data_migration && <span style={{ padding: '2px 7px', borderRadius: 4, background: '#DBEAFE', color: '#1D4ED8', fontSize: 10, fontWeight: 600 }}>📊 Data: {plan.data_migration}</span>}
          </div>
          {/* Design patterns */}
          {plan.design_patterns?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
              <span style={{ fontSize: 9, fontWeight: 700, color: '#57606A' }}>Patterns:</span>
              {plan.design_patterns.map((p, i) => (
                <span key={i} style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: '#DBEAFE', color: '#1D4ED8' }}>{p}</span>
              ))}
            </div>
          )}
          {/* Suggested/Cloud services */}
          {(plan.suggested_services || plan.cloud_services)?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
              <span style={{ fontSize: 9, fontWeight: 700, color: '#57606A' }}>Services:</span>
              {(plan.suggested_services || plan.cloud_services).map((s, i) => (
                <span key={i} style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: '#ECFEFF', color: '#06B6D4' }}>{s}</span>
              ))}
            </div>
          )}
          {/* Alternatives (for Repurchase) */}
          {plan.alternatives?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
              <span style={{ fontSize: 9, fontWeight: 700, color: '#57606A' }}>Alternatives:</span>
              {plan.alternatives.map((a, i) => (
                <span key={i} style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: '#FBEFFF', color: '#6639BA' }}>{a}</span>
              ))}
            </div>
          )}
          {/* Steps */}
          {(plan.steps || plan.decommission_steps || []).length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#57606A', marginBottom: 4 }}>STEPS:</div>
              {(plan.steps || plan.decommission_steps).map((step, i) => (
                <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 3 }}>
                  <span style={{ fontSize: 10, color: optColor, fontWeight: 700, minWidth: 14 }}>{i + 1}.</span>
                  <span style={{ fontSize: 10, color: '#1A1F2E' }}>{step}</span>
                </div>
              ))}
            </div>
          )}
          {/* Managed services */}
          {plan.managed_services?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
              {plan.managed_services.map((svc, i) => (
                <span key={i} style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: '#ECFEFF', color: '#06B6D4', border: '1px solid #06B6D420' }}>{svc}</span>
              ))}
            </div>
          )}
          {/* Risks */}
          {plan.risks?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {plan.risks.map((r, i) => (
                <span key={i} style={{ fontSize: 9, padding: '2px 6px', borderRadius: 4, background: '#FFEBE9', color: '#CF222E' }}>⚠ {r}</span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ── SDLC Blueprint panel (inside Refactor/Replatform score rows) ────────── */
function SdlcBlueprintPanel({ blueprint: bp, opt, optColor }) {
  const [open, setOpen] = React.useState(false);
  if (!bp || Object.keys(bp).length === 0) return null;

  const cloudColor = bp.cloud?.platform === 'Azure' ? '#0078D4'
                   : bp.cloud?.platform === 'AWS'   ? '#FF9900' : '#4285F4';

  const Section = ({ title, children }) => (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#57606A', textTransform: 'uppercase',
        letterSpacing: '0.5px', marginBottom: 4 }}>{title}</div>
      {children}
    </div>
  );

  const Tag = ({ text, color }) => (
    <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4,
      background: (color || optColor) + '18', color: color || optColor,
      border: `1px solid ${(color || optColor)}33`, marginRight: 4, marginBottom: 3, display: 'inline-block' }}>
      {text}
    </span>
  );

  const Row = ({ label, value }) => value && value !== 'null' ? (
    <div style={{ display: 'flex', gap: 6, fontSize: 11, marginBottom: 2 }}>
      <span style={{ color: '#57606A', minWidth: 90, flexShrink: 0 }}>{label}:</span>
      <span style={{ color: '#1A1F2E', fontWeight: 500 }}>{value}</span>
    </div>
  ) : null;

  return (
    <div style={{ borderTop: `1px solid ${optColor}22`, background: optColor + '08' }}>
      <div onClick={() => setOpen(o => !o)} style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px 6px 38px',
        cursor: 'pointer', fontSize: 11, color: optColor, fontWeight: 700 }}>
        <span>📋</span>
        <span>Full SDLC Blueprint — {opt === 'Refactor' ? 'Incremental Modernization' : 'Clean Rebuild'}</span>
        <span style={{ marginLeft: 'auto', fontSize: 10 }}>{open ? '▲ collapse' : '▼ expand'}</span>
      </div>
      {open && (
        <div style={{ padding: '0 16px 14px 38px', display: 'flex', flexDirection: 'column', gap: 0 }}>
          {bp.approach && (
            <div style={{ fontSize: 11, color: optColor, fontWeight: 600, marginBottom: 10,
              padding: '6px 10px', background: optColor + '12', borderRadius: 6, lineHeight: 1.5 }}>
              {bp.approach}
            </div>
          )}
          {bp.stack && (
            <Section title="Application Stack">
              <Row label="Language" value={bp.stack.language} />
              <Row label="Framework" value={bp.stack.framework} />
              {bp.stack.key_libraries?.length > 0 && <div style={{ marginTop: 3 }}>{bp.stack.key_libraries.map((l, i) => <Tag key={i} text={l} />)}</div>}
              {bp.stack.reasoning && <div style={{ fontSize: 11, color: '#57606A', marginTop: 4, lineHeight: 1.4 }}>{bp.stack.reasoning}</div>}
            </Section>
          )}
          {bp.data && (
            <Section title="Data Layer">
              <Row label="Database" value={bp.data.database} />
              <Row label="ORM" value={bp.data.orm} />
              <Row label="Migration" value={bp.data.migration} />
            </Section>
          )}
          {bp.cloud && (
            <Section title={`Cloud — ${bp.cloud.platform || ''}`}>
              {bp.cloud.reasoning && <div style={{ fontSize: 11, color: cloudColor, fontWeight: 600, marginBottom: 6, lineHeight: 1.4 }}>Why {bp.cloud.platform}: {bp.cloud.reasoning}</div>}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                {[['Compute', bp.cloud.compute], ['Database', bp.cloud.database], ['Auth', bp.cloud.auth],
                  ['API Gateway', bp.cloud.api_gateway], ['Storage', bp.cloud.storage],
                  ['Messaging', bp.cloud.messaging], ['IaC', bp.cloud.iac]].map(([k, v]) =>
                  v && v !== 'null' ? <div key={k} style={{ fontSize: 11 }}><span style={{ color: '#57606A' }}>{k}:</span> <span style={{ fontWeight: 500 }}>{v}</span></div> : null
                )}
              </div>
            </Section>
          )}
          {bp.architecture && (
            <Section title="Architecture">
              <Row label="Style" value={bp.architecture.style} />
              {bp.architecture.patterns?.length > 0 && <div style={{ marginTop: 3 }}>{bp.architecture.patterns.map((p, i) => <Tag key={i} text={p} />)}</div>}
              {bp.architecture.bounded_contexts?.length > 0 && <div style={{ fontSize: 11, color: '#57606A', marginTop: 4 }}>Bounded contexts: {bp.architecture.bounded_contexts.join(', ')}</div>}
            </Section>
          )}
          {bp.devops && (
            <Section title="DevOps & CI/CD">
              <Row label="Source control" value={bp.devops.source_control} />
              <Row label="CI/CD" value={bp.devops.ci_cd} />
              <Row label="Branching" value={bp.devops.branching} />
              <Row label="Containers" value={bp.devops.containers} />
            </Section>
          )}
          {bp.testing && (
            <Section title="Testing Strategy">
              <Row label="Unit" value={bp.testing.unit} />
              <Row label="Integration" value={bp.testing.integration} />
              <Row label="E2E" value={bp.testing.e2e} />
              <Row label="SAST" value={bp.testing.security_sast} />
            </Section>
          )}
          {bp.security && (
            <Section title="Security">
              <Row label="Auth" value={bp.security.auth} />
              <Row label="Secrets" value={bp.security.secrets} />
              <Row label="Compliance" value={bp.security.compliance} />
            </Section>
          )}
          {bp.observability && (
            <Section title="Observability">
              <Row label="Logging" value={bp.observability.logging} />
              <Row label="Metrics" value={bp.observability.metrics} />
              <Row label="Tracing" value={bp.observability.tracing} />
            </Section>
          )}
          {bp.migration_phases?.length > 0 && (
            <Section title="Migration Phases">
              {bp.migration_phases.map((ph, i) => (
                <div key={i} style={{ fontSize: 11, marginBottom: 4, padding: '4px 8px',
                  background: '#fff', borderRadius: 4, border: '1px solid #D0D7DE' }}>
                  <strong style={{ color: optColor }}>Phase {ph.phase}</strong>
                  {ph.duration && <span style={{ color: '#57606A', marginLeft: 6 }}>({ph.duration})</span>}
                  {' — '}{ph.focus}
                  {ph.rationale && <div style={{ color: '#57606A', marginTop: 2 }}>{ph.rationale}</div>}
                </div>
              ))}
            </Section>
          )}
          {(bp.team || bp.timeline) && (
            <Section title="Team & Timeline">
              <Row label="Team" value={bp.team} />
              <Row label="Timeline" value={bp.timeline} />
            </Section>
          )}
          {bp.risks?.length > 0 && (
            <Section title="Key Risks">
              {bp.risks.map((r, i) => <div key={i} style={{ fontSize: 11, color: '#CF222E', marginBottom: 2 }}>⚠ {r}</div>)}
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function ArchitecturePlanPanel({ plan }) {
  const [open, setOpen] = React.useState(false);
  if (!plan?.style) return null;
  const cloudColor = plan.cloud_platform === 'Azure' ? '#0078D4'
                   : plan.cloud_platform === 'AWS'   ? '#FF9900' : '#4285F4';
  return (
    <div style={{ borderBottom: '1px solid #D0D7DE', background: '#F6F8FA' }}>
      <div onClick={() => setOpen(o => !o)} style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', cursor: 'pointer' }}>
        <span style={{ fontSize: 14 }}>🏗️</span>
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#1A1F2E' }}>Architecture: {plan.style}</span>
          <span style={{ fontSize: 11, color: cloudColor, fontWeight: 700, marginLeft: 10,
            padding: '1px 7px', borderRadius: 4, background: cloudColor + '18' }}>{plan.cloud_platform}</span>
          {plan.estimated_timeline && (
            <span style={{ fontSize: 11, color: '#57606A', marginLeft: 8 }}>
              · {plan.estimated_timeline} · {plan.team_size}
            </span>
          )}
        </div>
        <span style={{ fontSize: 10, color: '#57606A' }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ padding: '0 16px 12px 38px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {plan.cloud_reasoning && (
            <div style={{ fontSize: 11, color: '#57606A', lineHeight: 1.5 }}>
              <strong style={{ color: cloudColor }}>Why {plan.cloud_platform}:</strong> {plan.cloud_reasoning}
            </div>
          )}
          {plan.design_patterns?.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#57606A', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Design Patterns</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {plan.design_patterns.map((p, i) => (
                  <span key={i} style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 4, background: '#EFF2F5', color: '#0969DA', border: '1px solid #D0D7DE' }}>{p}</span>
                ))}
              </div>
            </div>
          )}
          {plan.key_services && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#57606A', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Cloud Services</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                {Object.entries(plan.key_services).map(([k, v]) => v && (
                  <div key={k} style={{ fontSize: 11 }}>
                    <span style={{ color: '#57606A', textTransform: 'capitalize' }}>{k}:</span> {v}
                  </div>
                ))}
              </div>
            </div>
          )}
          {plan.migration_phases?.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#57606A', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Migration: {plan.migration_strategy}</div>
              {plan.migration_phases.map((ph, i) => (
                <div key={i} style={{ fontSize: 11, color: '#57606A', marginBottom: 2 }}>
                  <strong style={{ color: '#1A1F2E' }}>Phase {ph.phase}:</strong> {ph.modules?.join(', ')} — {ph.rationale}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StackMigrationPanel({ original, stackAnalysis, targetStack }) {
  const [open, setOpen] = React.useState(false);
  if (!stackAnalysis) return null;
  // Hide if target stack is empty, N/A, or not applicable
  const lang = targetStack?.language || '';
  if (!lang || lang.toLowerCase() === 'n/a' || lang.toLowerCase() === 'none') return null;
  const isMigrating  = !stackAnalysis.stay_in_original;
  const target       = `${targetStack.language}/${targetStack.framework}`;
  const alternatives = stackAnalysis.alternatives_considered || [];
  return (
    <div style={{ borderBottom: '1px solid #D0D7DE', background: isMigrating ? '#F0FFF4' : '#F6F8FA',
      borderLeft: `3px solid ${isMigrating ? '#1A7F37' : '#D0D7DE'}` }}>
      <div onClick={() => setOpen(o => !o)} style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 16px', cursor: 'pointer' }}>
        <span style={{ fontSize: 14 }}>{isMigrating ? '🔄' : '✅'}</span>
        <div style={{ flex: 1 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: isMigrating ? '#1A7F37' : '#57606A' }}>
            {isMigrating ? `Stack migration: ${original} → ${target}` : `Stay in original stack: ${target}`}
          </span>
          {targetStack.reasoning && (
            <span style={{ fontSize: 11, color: '#57606A', marginLeft: 8 }}>— {targetStack.reasoning.slice(0, 70)}</span>
          )}
        </div>
        {alternatives.length > 0 && <span style={{ fontSize: 10, color: '#57606A' }}>{alternatives.length} alternatives {open ? '▲' : '▼'}</span>}
      </div>
      {open && (
        <div style={{ padding: '0 16px 10px 38px' }}>
          {isMigrating && stackAnalysis.reason_for_change && (
            <div style={{ fontSize: 11, color: '#1A7F37', marginBottom: 8, lineHeight: 1.5 }}>
              <strong>Why migrate:</strong> {stackAnalysis.reason_for_change}
            </div>
          )}
          {alternatives.map((alt, i) => {
            const isChosen = alt.language === targetStack.language;
            return (
              <div key={i} style={{ padding: '6px 10px', borderRadius: 6, marginBottom: 4,
                border: `1px solid ${isChosen ? '#1A7F37' : '#D0D7DE'}`,
                background: isChosen ? '#DAFBE1' : '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: isChosen ? '#1A7F37' : '#1A1F2E' }}>
                    {alt.language}/{alt.framework}
                  </span>
                  {isChosen && <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: '#1A7F37', color: '#fff' }}>CHOSEN</span>}
                  <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 700, color: alt.score >= 7 ? '#1A7F37' : '#9A6700' }}>{alt.score?.toFixed(1)}/10</span>
                </div>
                <div style={{ display: 'flex', gap: 12, fontSize: 11 }}>
                  {alt.pros && <div style={{ flex: 1, color: '#1A7F37' }}><strong>✓</strong> {alt.pros}</div>}
                  {alt.cons && <div style={{ flex: 1, color: '#CF222E' }}><strong>✗</strong> {alt.cons}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AppDecisionCard({ app, decision, isSelected, onToggleSelect, onChange, disabled, selectedModules, onModulesChange }) {
  const scores   = app.scores || {};
  const maxScore = Math.max(...Object.values(scores).map(s => s?.score || 0));

  // Modules come from key_modules populated by discovery agent from actual codebase
  // Format: ["loans: Loan lifecycle management", "clients: Client registration", ...]
  const modules = React.useMemo(() => {
    const mods = [];
    // Primary: key_modules from discovery agent (real codebase analysis)
    const keyMods = app.key_modules || [];
    keyMods.forEach(entry => {
      // entry is either "id: description" or just "name"
      const colonIdx = entry.indexOf(':');
      if (colonIdx > 0) {
        const id   = entry.slice(0, colonIdx).trim().toLowerCase().replace(/\s+/g, '_');
        const name = entry.slice(colonIdx + 1).trim() || id;
        if (!mods.find(m => m.id === id)) mods.push({ id, name: id.charAt(0).toUpperCase() + id.slice(1).replace(/_/g, ' '), desc: name });
      } else {
        const id = entry.trim().toLowerCase().replace(/\s+/g, '_');
        if (!mods.find(m => m.id === id)) mods.push({ id, name: entry.trim(), desc: '' });
      }
    });
    // Fallback: parse module_map lines like "  Loans (12 files) — Loan lifecycle"
    if (mods.length === 0 && app.module_map) {
      app.module_map.split('\n').forEach(line => {
        const m = line.match(/^\s*(\w[\w\s]*?)\s*\(\d+\s*files?\)/i);
        if (m) {
          const name = m[1].trim();
          const id   = name.toLowerCase().replace(/\s+/g, '_');
          const desc = line.split('—')[1]?.trim() || '';
          if (!mods.find(x => x.id === id)) mods.push({ id, name, desc });
        }
      });
    }
    return mods;
  }, [app.key_modules, app.module_map]);

  return (
    <div style={{
      border: `2px solid ${isSelected ? '#0969DA' : '#D0D7DE'}`,
      borderRadius: 10, overflow: 'hidden',
      background: isSelected ? '#fff' : '#F6F8FA',
      boxShadow: isSelected ? '0 2px 8px rgba(9,105,218,0.12)' : 'none',
      opacity: isSelected ? 1 : 0.55, transition: 'all 0.2s',
    }}>

      {/* Header */}
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #D0D7DE',
        background: isSelected ? '#F6F8FA' : '#EFF2F5',
        display: 'flex', alignItems: 'center', gap: 10 }}>
        <div onClick={onToggleSelect} style={{
          width: 20, height: 20, borderRadius: 5, flexShrink: 0,
          border: `2px solid ${isSelected ? '#0969DA' : '#D0D7DE'}`,
          background: isSelected ? '#0969DA' : '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', transition: 'all 0.15s',
        }}>
          {isSelected && <span style={{ color: '#fff', fontSize: 12, lineHeight: 1 }}>✓</span>}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: isSelected ? '#0969DA' : '#57606A' }}>
            {app.name}
            {app.uncertain && (
              <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 700, padding: '1px 6px',
                borderRadius: 6, background: '#FFF8C5', color: '#9A6700', border: '1px solid #9A670044' }}>
                ⚠ uncertain
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: '#57606A', marginTop: 1 }}>
            {app.language} · {app.framework} · {app.loc?.toLocaleString()} lines of code
          </div>
        </div>
      </div>

      {/* AI overall reasoning */}
      {app.reasoning && (
        <div style={{ padding: '10px 16px', borderBottom: '1px solid #D0D7DE',
          background: '#F6F8FA', fontSize: 12, color: '#1A1F2E', lineHeight: 1.6 }}>
          {app.reasoning}
        </div>
      )}

      {/* Stack migration — only show if target stack has actual data */}
      {app.stack_analysis && app.target_stack && (app.target_stack.language || app.target_stack.reasoning) && (
        <StackMigrationPanel
          original={`${app.language}/${app.framework}`}
          stackAnalysis={app.stack_analysis}
          targetStack={app.target_stack}
        />
      )}

      {/* Architecture plan */}
      {app.architecture_plan?.style && (
        <ArchitecturePlanPanel plan={app.architecture_plan} />
      )}

      {/* Scoring matrix */}
      <div style={{ padding: '10px 16px', borderBottom: '1px solid #D0D7DE' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#57606A',
          textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 8 }}>
          AI Scoring — all options evaluated
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {/* Show all options from scores + all 7Rs */}
          {[...new Set([...DECISION_OPTIONS, ...Object.keys(scores)])].map(opt => {
            const s        = scores[opt] || { score: 0, justification: '' };
            const isTop    = s.score === maxScore && s.score > 0;
            const isChosen = decision === opt;
            const optColor = OPT_COLOR[opt] || '#57606A';
            const optBg    = OPT_BG[opt] || '#F6F8FA';
            const pct      = Math.round((s.score / 10) * 100);
            return (
              <div key={opt}
                onClick={() => { onChange(opt); if (!isSelected) onToggleSelect(); }}
                style={{
                  border: `1.5px solid ${isChosen ? optColor : isTop ? optColor + '88' : '#D0D7DE'}`,
                  borderRadius: 7, overflow: 'hidden',
                  background: isChosen ? optBg : isTop ? optBg + '55' : '#fff',
                  cursor: 'pointer',
                  boxShadow: isChosen ? `0 0 0 2px ${optColor}44` : 'none',
                  transition: 'all 0.15s',
                }}>
                {/* Score bar */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px' }}>
                  <span style={{ fontSize: 12, width: 16 }}>{OPT_ICON[opt]}</span>
                  <span style={{ fontSize: 12, fontWeight: isTop || isChosen ? 700 : 500,
                    color: isChosen ? optColor : isTop ? optColor : '#57606A', width: 62, flexShrink: 0 }}>
                    {opt}
                  </span>
                  <div style={{ flex: 1, height: 8, background: '#EFF2F5', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: optColor,
                      borderRadius: 4, transition: 'width 0.6s' }} />
                  </div>
                  <span style={{ fontSize: 12, fontWeight: 700, color: optColor,
                    width: 28, textAlign: 'right', flexShrink: 0 }}>
                    {s.score?.toFixed(1)}
                  </span>
                  {isChosen && (
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px',
                      borderRadius: 6, background: optColor, color: '#fff', flexShrink: 0 }}>
                      ✓ YOUR PICK
                    </span>
                  )}
                  {!isChosen && isTop && (
                    <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px',
                      borderRadius: 6, background: optColor, color: '#fff', flexShrink: 0 }}>
                      AI TOP
                    </span>
                  )}
                </div>
                {/* Justification */}
                {s.justification && (
                  <div style={{ padding: '0 10px 7px 38px', fontSize: 11,
                    color: isTop ? '#1A1F2E' : '#57606A', lineHeight: 1.5 }}>
                    {s.justification}
                  </div>
                )}
                {/* SDLC Blueprint for Refactor/Replatform */}
                {(opt === 'Refactor' || opt === 'Replatform') && s.sdlc_blueprint && Object.keys(s.sdlc_blueprint).length > 0 && (
                  <SdlcBlueprintPanel blueprint={s.sdlc_blueprint} opt={opt} optColor={optColor} />
                )}
                {/* Repurchase: product cards */}
                {(opt === 'Replace' || opt === 'Repurchase') && s.products?.length > 0 && (
                  <div style={{ padding: '0 10px 10px 38px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {s.products.map((p, i) => (
                      <a key={i} href={`https://${p.url}`} target="_blank" rel="noreferrer"
                        style={{ textDecoration: 'none' }}>
                        <div style={{
                          border: '1px solid #D0D7DE', borderRadius: 7, padding: '6px 10px',
                          background: '#fff', minWidth: 130, cursor: 'pointer',
                          transition: 'border-color 0.15s, box-shadow 0.15s',
                        }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = optColor; e.currentTarget.style.boxShadow = `0 2px 8px ${optColor}22`; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = '#D0D7DE'; e.currentTarget.style.boxShadow = 'none'; }}
                        >
                          <div style={{ fontWeight: 700, fontSize: 12, color: '#1A1F2E' }}>{p.name}</div>
                          <div style={{ fontSize: 10, color: optColor, fontWeight: 600, marginTop: 1 }}>{p.type}</div>
                          <div style={{ fontSize: 10, color: '#57606A', marginTop: 3, lineHeight: 1.4 }}>{p.cost_model}</div>
                          <div style={{ fontSize: 10, color: '#0969DA', marginTop: 3 }}>↗ {p.url}</div>
                        </div>
                      </a>
                    ))}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignSelf: 'center' }}>
                      {s.low_code_viable && (
                        <div style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
                          background: '#FBEFFF', color: '#6639BA', border: '1px solid #6639BA44' }}>
                          ✓ Low-code viable
                        </div>
                      )}
                      {s.migration_effort && (
                        <div style={{ fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 6,
                          background: '#EFF2F5', color: '#57606A', border: '1px solid #D0D7DE' }}>
                          Migration: {s.migration_effort}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {/* Migration Plan for this R (expandable) */}
                {app.migration_plans && app.migration_plans[opt] && (
                  <MigrationPlanPanel plan={app.migration_plans[opt]} opt={opt} optColor={optColor} />
                )}
              </div>
            );
          })}
        </div>

        {/* Risks */}
        {app.risks?.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {app.risks.map((r, i) => (
              <span key={i} style={{ fontSize: 10, padding: '2px 7px', borderRadius: 8,
                background: '#FFEBE9', color: '#CF222E', border: '1px solid rgba(207,34,46,0.2)' }}>
                ⚠ {r}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Decision buttons */}
      <div style={{ padding: '10px 16px' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#57606A',
          textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 7 }}>
          Your Decision — choose any option
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {DECISION_OPTIONS.map(opt => {
            const isChosen = decision === opt;
            const optColor = OPT_COLOR[opt];
            const optBg    = OPT_BG[opt];
            return (
              <button key={opt}
                disabled={disabled}
                onClick={() => {
                  onChange(opt);
                  // Auto-select the card when user picks a decision
                  if (!isSelected) onToggleSelect();
                }}
                style={{
                  flex: 1, padding: '8px 4px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                  border: `2px solid ${isChosen ? optColor : '#D0D7DE'}`,
                  background: isChosen ? optBg : '#fff',
                  color: isChosen ? optColor : '#57606A',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3,
                  boxShadow: isChosen ? `0 0 0 3px ${optColor}33` : 'none',
                }}
                onMouseEnter={e => { if (!isChosen) { e.currentTarget.style.borderColor = optColor; e.currentTarget.style.background = optBg + '88'; e.currentTarget.style.color = optColor; } }}
                onMouseLeave={e => { if (!isChosen) { e.currentTarget.style.borderColor = '#D0D7DE'; e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#57606A'; } }}
              >
                <span style={{ fontSize: 14 }}>{OPT_ICON[opt]}</span>
                <span>{opt}</span>
                {isChosen && <span style={{ fontSize: 9, opacity: 0.8 }}>✓ selected</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Module Selection — only shown for Refactor/New decisions */}
      {(decision === 'Refactor' || decision === 'New') && modules.length > 0 && (
        <div style={{ padding: '10px 16px', borderTop: '1px solid #D0D7DE', background: '#F6FBFF' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: '#0969DA',
              textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              🧩 Select Modules to Generate
            </span>
            <span style={{ fontSize: 10, color: '#57606A' }}>
              ({selectedModules?.size > 0 ? selectedModules.size : modules.length} of {modules.length})
            </span>
            <button onClick={() => onModulesChange(new Set(modules.map(m => m.id)))} style={{
              marginLeft: 'auto', fontSize: 10, padding: '2px 8px', borderRadius: 5,
              border: '1px solid #D0D7DE', background: '#fff', cursor: 'pointer', color: '#57606A' }}>
              All
            </button>
            <button onClick={() => onModulesChange(new Set())} style={{
              fontSize: 10, padding: '2px 8px', borderRadius: 5,
              border: '1px solid #D0D7DE', background: '#fff', cursor: 'pointer', color: '#57606A' }}>
              None
            </button>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {modules.map(mod => {
              const isModSelected = !selectedModules || selectedModules.size === 0 || selectedModules.has(mod.id);
              return (
                <div key={mod.id}
                  onClick={() => {
                    const next = new Set(selectedModules?.size > 0 ? selectedModules : modules.map(m => m.id));
                    next.has(mod.id) ? next.delete(mod.id) : next.add(mod.id);
                    // If all selected, treat as "all" (empty set)
                    if (next.size === modules.length) onModulesChange(new Set());
                    else onModulesChange(next);
                  }}
                  title={mod.desc || mod.name}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '4px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                    cursor: 'pointer', userSelect: 'none',
                    border: `1.5px solid ${isModSelected ? '#0969DA' : '#D0D7DE'}`,
                    background: isModSelected ? '#DDF4FF' : '#F6F8FA',
                    color: isModSelected ? '#0969DA' : '#8B949E',
                    transition: 'all 0.15s',
                  }}>
                  <span style={{ fontSize: 10 }}>{isModSelected ? '✓' : '○'}</span>
                  <span>{mod.name}</span>
                </div>
              );
            })}
          </div>
          {selectedModules?.size > 0 && selectedModules.size < modules.length && (
            <div style={{ marginTop: 6, fontSize: 10, color: '#9A6700', fontWeight: 600 }}>
              ⚡ Only selected modules will have requirements + code generated — faster run
            </div>
          )}
        </div>
      )}
    </div>
  );
}
